import React from "react"

import { useStaticQuery, graphql } from "gatsby"
import { StaticImage } from "gatsby-plugin-image"
import Image from "gatsby-plugin-sanity-image"
import PropTypes from "prop-types"
import { YouTubeEmbed } from "react-social-media-embed"

import * as styles from "./hero.module.scss"
import DarkColor from "../../../helpers/dark-color"
import Effect1Svg from "../../../images/effects/effect3.svg"
import Effect2Svg from "../../../images/effects/effect4.svg"
import CopyContainer from "../../atoms/copy-container"
import Heading from "../../atoms/heading"
import Illustration from "../../atoms/illustration"
import Tag from "../../atoms/tag"
import Wrapper from "../../atoms/wrapper"
import ButtonGroup from "../button-group"

const Hero = ({
  title,
  description,
  ctas,
  image,
  video,
  color,
  colorHex,
  partner,
  border,
  illustration,
  alignment,
  full,
  styleVariant,
  bgColor,
  aspectRatio,
  presentingPartner,
}) => {
  const { prefix } = useStaticQuery(graphql`
    {
      prefix: sanityDictionary(key: { current: { eq: "partner_feature" } }) {
        value
      }
    }
  `)

  const imgElement = image ? (
    <Image
      alt=""
      {...image}
      width={600}
      className={`${styles.image} ${full || !color ? styles.image__full : ""}`}
      style={!border && aspectRatio ? { aspectRatio } : {}}
    />
  ) : (
    <StaticImage
      className={styles.image}
      src="../../../images/scenes/star.jpg"
      alt=""
    />
  )

  const contents = (
    <>
      <div className={styles.left}>
        {partner ? (
          <div>
            <Tag partner>
              {partner.prefix || prefix?.value}
              {partner.logo ? (
                <Image {...partner.logo} width={80} alt={partner.name} />
              ) : (
                <> {partner.name}</>
              )}
            </Tag>
          </div>
        ) : (
          ""
        )}
        {title ? <Heading>{title}</Heading> : ""}
        <CopyContainer
          className={!full ? styles.description : ""}
          content={description}
          bg={color}
        />
        {ctas?.length > 0 ? (
          <ButtonGroup
            ctas={ctas}
            large={ctas.length === 1}
            partner={partner || presentingPartner}
          />
        ) : (
          ""
        )}
      </div>
      <div className={styles.right}>
        {styleVariant === "effect" && (full || !color) ? (
          <img
            className={`${styles.effect} ${styles.effect__1} ${color ? styles.effect__1color : ""}`}
            src={Effect1Svg}
            alt=""
          />
        ) : (
          ""
        )}
        {video ? <YouTubeEmbed width="100%" url={video} /> : imgElement}
        {styleVariant === "effect" && (full || !color) ? (
          <img
            className={`${styles.effect} ${styles.effect__2} ${color ? styles.effect__2color : ""}`}
            src={Effect2Svg}
            alt=""
          />
        ) : (
          ""
        )}
      </div>
      {illustration ? (
        <div className={styles.illustration}>
          <Illustration icon={illustration} size="l" />
        </div>
      ) : (
        ""
      )}
    </>
  )

  return full || !color ? (
    <div
      className={`${styles.hero} ${color ? styles.hero__full : styles.hero__open} ${styleVariant ? styles[`hero__${styleVariant}`] : ""} ${styleVariant === "grid" && DarkColor(bgColor) ? styles[`hero__${styleVariant}Light`] : ""} ${color && color !== "partner" ? styles[`hero__${color.replaceAll("-", "")}`] : ""} ${
        color === "partner" && colorHex && DarkColor(colorHex, true)
          ? styles.hero__dark
          : ""
      }`}
      style={
        color === "partner" && colorHex ? { backgroundColor: colorHex } : {}
      }
    >
      <Wrapper
        flex="desktop"
        container
        className={`${
          alignment === "image_left_text_right" ? styles.hero__flip : ""
        } ${styleVariant === "grid" ? styles[`gridWrapper__${(color || bgColor).replaceAll("-", "")}`] : ""}`}
      >
        {contents}
      </Wrapper>
    </div>
  ) : (
    <Wrapper container spacer>
      <div
        className={`${styles.hero} ${
          alignment === "image_left_text_right" ? styles.hero__flip : ""
        } ${color !== "partner" ? styles[`hero__${color}`] : ""} ${
          color === "partner" && colorHex && DarkColor(colorHex, true)
            ? styles.hero__dark
            : ""
        }`}
        style={
          color === "partner" && colorHex ? { backgroundColor: colorHex } : {}
        }
      >
        {contents}
      </div>
    </Wrapper>
  )
}

export default Hero

Hero.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string.isRequired,
  ctas: PropTypes.array.isRequired,
  image: PropTypes.string,
  video: PropTypes.string,
  color: PropTypes.string,
  colorHex: PropTypes.string,
  partner: PropTypes.object,
  border: PropTypes.bool,
  alignment: PropTypes.string,
  full: PropTypes.bool,
  illustration: PropTypes.string,
  styleVariant: PropTypes.string,
  bgColor: PropTypes.string,
  aspectRatio: PropTypes.string,
  presentingPartner: PropTypes.object,
}

Hero.defaultProps = {
  title: "",
  color: null,
  colorHex: "",
  partner: null,
  image: null,
  video: null,
  border: true,
  alignment: "text_left_image_right",
  full: false,
  illustration: "",
  styleVariant: "",
  bgColor: "paper",
  aspectRatio: "",
  presentingPartner: null,
}
